'use client';

import { useAppShell } from '@/components/layout/app-shell';
import { resolveContextualSidebar } from '@/components/sidebar/contextual/registry';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
} from '@/components/ui/sidebar';
import { canSeePaidDms, canSeeTravelKnowledge } from '@/shared/lib/auth/permissions';
import { StudioMessagesBadge } from './studio-messages-badge';
import type { FeatureName } from '@/shared/lib/config/feature-flags';
import { isFeatureEnabled } from '@/shared/lib/config/feature-flags';
import { useAuthStore } from '@/shared/stores/auth-store';
import {
  BarChart3,
  Compass,
  Film,
  LayoutDashboard,
  MessageSquare,
  Settings,
  Ticket,
  Wallet,
  Wand2,
} from 'lucide-react';
import Link from 'next/link';
import { useChromePathname } from '@/shared/components/providers/chrome-pathname';

import { CopyrightYear } from '@/components/ui/copyright-year';

const studioNavigation = [
  { name: 'Dashboard', href: '/studio', icon: LayoutDashboard },
  { name: 'Content', href: '/studio/content', icon: Film },
  { name: 'Messages', href: '/studio/messages', icon: MessageSquare },
  { name: 'Customization', href: '/studio/customization', icon: Wand2 },
  { name: 'Travel Knowledge', href: '/studio/travel-knowledge', icon: Compass },
  { name: 'Analytics', href: '/studio/analytics', icon: BarChart3 },
  { name: 'Payouts', href: '/studio/payouts', icon: Wallet },
  { name: 'Settings', href: '/studio/settings', icon: Settings },
  { name: 'Codes & Links', href: '/studio/codes', icon: Ticket },
];

const featureFlagMap: Partial<Record<string, FeatureName>> = {
  '/studio/analytics': 'STUDIO_ANALYTICS',
  '/studio/payouts': 'STUDIO_PAYOUTS',
  '/studio/settings': 'STUDIO_SETTINGS',
  '/studio/codes': 'STUDIO_CODES',
};

export function StudioSidebar({ collapsible, ...props }: React.ComponentProps<typeof Sidebar>) {
  // Null at prerender/first render → default nav, no active state; the
  // pre-paint script stamps data-active and the data-contextual CSS guard
  // hides the content on contextual routes until the resolved render.
  const pathname = useChromePathname();
  const { isImmersive } = useAppShell();
  const user = useAuthStore((s) => s.user);
  const resolvedCollapsible = collapsible ?? (isImmersive ? 'offcanvas' : 'icon');

  // Context-scoped sidebar wins when route matches a registry entry
  // (e.g. inside a course, the sidebar morphs to course-scoped nav). Outer
  // Sidebar wrapper + SidebarRail stay so the shell behaves identically;
  // only the inner content swaps.
  const contextual = pathname !== null ? resolveContextualSidebar(pathname) : null;
  if (contextual) {
    const { Component, paramValue } = contextual;
    return (
      <Sidebar collapsible={resolvedCollapsible} className="studio-sidebar-bordered" {...props}>
        <Component paramValue={paramValue} />
        <SidebarRail />
      </Sidebar>
    );
  }

  const isActive = (href: string) => {
    if (pathname === null) return false;
    if (href === '/studio') {
      return pathname === '/studio';
    }
    // For Content, match content and posts paths (posts is a sub-section of content)
    if (href === '/studio/content') {
      return pathname.startsWith('/studio/content') || pathname.startsWith('/studio/posts');
    }
    return pathname.startsWith(href);
  };

  // Mirrors isActive() for the pre-paint stamping script.
  const activeMatchAttrs = (href: string) => ({
    'data-active-match': href === '/studio' ? ('exact' as const) : ('prefix' as const),
    ...(href === '/studio/content' ? { 'data-active-alias': '/studio/posts' } : {}),
  });

  return (
    <Sidebar collapsible={resolvedCollapsible} className="studio-sidebar-bordered" {...props}>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {studioNavigation
                .filter((item) => {
                  if (item.href === '/studio/travel-knowledge') {
                    return canSeeTravelKnowledge(user);
                  }
                  // TEMP tester gate (FRONT-307 rollout).
                  if (item.href === '/studio/messages') {
                    return canSeePaidDms(user);
                  }
                  const flag = featureFlagMap[item.href];
                  return !flag || isFeatureEnabled(flag);
                })
                .map((item) => {
                  const active = isActive(item.href);
                  return (
                    <SidebarMenuItem key={item.name}>
                      <SidebarMenuButton asChild isActive={active} tooltip={item.name}>
                        <Link
                          href={item.href}
                          className="relative mx-auto"
                          {...activeMatchAttrs(item.href)}
                          suppressHydrationWarning
                        >
                          <item.icon />
                          <span>{item.name}</span>
                          {item.href === '/studio/messages' && <StudioMessagesBadge />}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarSeparator className="mt-auto" />
      </SidebarContent>

      <SidebarFooter>
        {/* CSS-gated on the group's data-state (pre-paint patched) instead of
            the cookie-seeded `open` — conditional render mismatches hydration
            when the prerendered shell is closed and the client opens. */}
        <div className="px-3 py-3 text-2xs font-medium text-sidebar-foreground/50 uppercase tracking-widest group-data-[state=collapsed]:hidden">
          <div className="flex gap-3 mb-2">
            <Link
              href="https://taboo.tv/terms-conditions"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-sidebar-foreground/80 transition-colors"
            >
              Terms
            </Link>
            <Link
              href="https://taboo.tv/contact-us"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-sidebar-foreground/80 transition-colors"
            >
              Help
            </Link>
          </div>
          <p>
            &copy; <CopyrightYear /> Taboo TV Studios LLC
          </p>
        </div>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
